import { useState, useRef, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileSpreadsheet, Upload, Loader2, ArrowRight,
  CheckCircle, XCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYSTEM_FIELDS = [
  { value: "dni", label: "DNI/Pasaporte *", required: true },
  { value: "name", label: "Nombre *", required: true },
  { value: "phone", label: "Teléfono *", required: true },
  { value: "email", label: "Email", required: false },
  { value: "address", label: "Dirección", required: false },
  { value: "city", label: "Ciudad/Población", required: false },
  { value: "source", label: "Proveedor/Fuente", required: false },
  { value: "notes", label: "Notas", required: false }
];

/**
 * Self-contained customer import dialog.
 * Props:
 *   open: boolean - visibility
 *   onOpenChange(open): called by Dialog when open state changes
 *   onImported(result): called after a successful import so parent can reload list
 *   onPlanLimit(modalData): called when backend returns 403 PLAN_LIMIT_EXCEEDED
 */
export default function CustomerImportDialog({ open, onOpenChange, onImported, onPlanLimit }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [fileColumns, setFileColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep(1);
    setFile(null);
    setData([]);
    setFileColumns([]);
    setMapping({});
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onOpenChange?.(false);
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    if (![".csv", ".xls", ".xlsx"].includes(ext)) {
      toast.error("Formato no válido. Usa CSV, XLS o XLSX");
      return;
    }
    setFile(f);
    parseFile(f);
  };

  const parseFile = async (f) => {
    setLoading(true);
    try {
      const arrayBuf = await f.arrayBuffer();
      const workbook = XLSX.read(arrayBuf, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
      if (json.length < 2) {
        toast.error("El archivo debe tener al menos cabecera y una fila de datos");
        return;
      }
      const headers = json[0].map((h, idx) => ({
        index: idx,
        name: String(h).trim() || `Columna ${idx + 1}`
      }));
      const rows = json.slice(1).filter((row) => row.some((cell) => cell !== ""));

      // Auto-map columns by name similarity
      const autoMapping = {};
      headers.forEach((header) => {
        const hl = header.name.toLowerCase();
        SYSTEM_FIELDS.forEach((field) => {
          const fl = field.label.toLowerCase().replace(" *", "");
          const match =
            hl === field.value || hl === fl || hl.includes(field.value) || hl.includes(fl) ||
            (field.value === "dni" && (hl.includes("documento") || hl.includes("pasaporte") || hl.includes("nif"))) ||
            (field.value === "name" && (hl.includes("nombre") || hl.includes("cliente"))) ||
            (field.value === "phone" && (hl.includes("telefono") || hl.includes("móvil") || hl.includes("movil"))) ||
            (field.value === "city" && (hl.includes("ciudad") || hl.includes("población") || hl.includes("poblacion") || hl.includes("localidad"))) ||
            (field.value === "address" && (hl.includes("direccion") || hl.includes("dirección") || hl.includes("domicilio"))) ||
            (field.value === "email" && (hl.includes("email") || hl.includes("correo") || hl.includes("e-mail"))) ||
            (field.value === "source" && (hl.includes("proveedor") || hl.includes("fuente") || hl.includes("origen"))) ||
            (field.value === "notes" && (hl.includes("nota") || hl.includes("observacion") || hl.includes("comentario")));
          if (match && !autoMapping[field.value]) autoMapping[field.value] = header.index;
        });
      });

      setFileColumns(headers);
      setData(rows);
      setMapping(autoMapping);
      setStep(2);
    } catch (err) {
      console.error("Error parsing file:", err);
      toast.error("Error al leer el archivo");
    } finally {
      setLoading(false);
    }
  };

  const mappedPreview = () =>
    data.slice(0, 5).map((row) => {
      const out = {};
      Object.entries(mapping).forEach(([field, colIdx]) => {
        if (colIdx !== undefined && colIdx !== null && colIdx !== -1) out[field] = row[colIdx] || "";
      });
      return out;
    });

  // Memoised derivatives so we don't recompute on every render while the
  // preview table is visible.
  const visibleFields = useMemo(
    () => SYSTEM_FIELDS.filter(
      (f) => mapping[f.value] !== undefined && mapping[f.value] !== -1
    ),
    [mapping]
  );
  const previewRows = useMemo(() => mappedPreview(), [data, mapping]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateMapping = () => {
    const required = SYSTEM_FIELDS.filter((f) => f.required).map((f) => f.value);
    const missing = required.filter((f) => mapping[f] === undefined || mapping[f] === null || mapping[f] === -1);
    if (missing.length > 0) {
      const labels = missing.map((f) => SYSTEM_FIELDS.find((sf) => sf.value === f)?.label).join(", ");
      toast.error(`Campos obligatorios sin mapear: ${labels}`);
      return false;
    }
    return true;
  };

  const goToPreview = () => { if (validateMapping()) setStep(3); };

  const executeImport = async () => {
    setLoading(true);
    try {
      const customers = data
        .map((row) => {
          const c = {};
          Object.entries(mapping).forEach(([field, colIdx]) => {
            if (colIdx !== undefined && colIdx !== null && colIdx !== -1) {
              let v = row[colIdx] || "";
              if (field === "dni") v = String(v).toUpperCase().trim();
              c[field] = v;
            }
          });
          return c;
        })
        .filter((c) => c.dni && c.name);

      if (customers.length === 0) {
        toast.error("No hay clientes válidos para importar. Verifica que DNI y Nombre estén mapeados.");
        setLoading(false);
        return;
      }

      const { data: resp } = await axios.post(
        `${API}/customers/import`,
        { customers },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setResult(resp);
      setStep(4);
      if (resp.imported > 0) {
        toast.success(`${resp.imported} clientes importados correctamente`);
        onImported?.(resp);
      }
    } catch (error) {
      console.error("Import error:", error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && typeof detail === "object" && detail?.error === "PLAN_LIMIT_EXCEEDED") {
        onPlanLimit?.({
          open: true,
          limitType: detail.limit_type,
          currentCount: detail.current_count,
          maxCount: detail.max_allowed,
          planType: detail.plan_type
        });
        return;
      }
      let msg = "Error al importar clientes";
      if (detail) msg = typeof detail === "string" ? detail : (detail.message || JSON.stringify(detail));
      else if (error.message) msg = error.message;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange?.(true) : handleClose())}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6 text-primary" />
            Importar Clientes
          </DialogTitle>
          <DialogDescription>
            Importa clientes desde un archivo CSV o Excel
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 py-4">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= s ? "bg-primary text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {step > s ? <CheckCircle className="h-5 w-5" /> : s}
              </div>
              {s < 4 && <div className={`w-12 h-1 mx-1 ${step > s ? "bg-primary" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="py-6">
            <div
              className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center hover:border-primary hover:bg-slate-50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="import-file-input"
              />
              <Upload className="h-12 w-12 mx-auto text-slate-600 mb-4" />
              <p className="text-lg font-semibold text-slate-700">Arrastra o haz clic para seleccionar</p>
              <p className="text-sm text-slate-500 mt-2">Formatos aceptados: CSV, XLS, XLSX</p>
              {loading && (
                <div className="mt-4">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                  <p className="text-sm text-slate-500 mt-2">Procesando archivo...</p>
                </div>
              )}
            </div>
            <div className="mt-6 p-4 bg-blue-500/10 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 font-medium mb-2">💡 Consejos para la importación:</p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• La primera fila debe contener los nombres de las columnas</li>
                <li>• Campos obligatorios: DNI, Nombre y Teléfono</li>
                <li>• Los duplicados por DNI serán detectados automáticamente</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 2: Mapping */}
        {step === 2 && (
          <div className="py-4 space-y-4">
            <div className="p-3 bg-amber-500/10 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>📄 Archivo:</strong> {file?.name} ({data.length} registros)
              </p>
            </div>
            <p className="text-sm text-slate-600">Asocia las columnas de tu archivo con los campos del sistema:</p>
            <div className="space-y-3">
              {SYSTEM_FIELDS.map((field) => (
                <div key={field.value} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50">
                  <div className="w-40 flex items-center gap-2">
                    <span className={`font-medium ${field.required ? "text-slate-900" : "text-slate-600"}`}>
                      {field.label}
                    </span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-600" />
                  <Select
                    value={mapping[field.value]?.toString() ?? "-1"}
                    onValueChange={(v) => setMapping({ ...mapping, [field.value]: v === "-1" ? -1 : parseInt(v, 10) })}
                  >
                    <SelectTrigger className="flex-1 h-11" data-testid={`mapping-${field.value}`}>
                      <SelectValue placeholder="Seleccionar columna..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-1">-- No importar --</SelectItem>
                      {fileColumns.map((col) => (
                        <SelectItem key={col.index} value={col.index.toString()}>
                          {col.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <DialogFooter className="pt-4">
              <Button variant="outline" onClick={reset}>Volver</Button>
              <Button onClick={goToPreview} data-testid="go-to-preview-btn">
                Previsualizar <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 3 && (
          <div className="py-4 space-y-4">
            <div className="p-3 bg-emerald-500/10 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800">
                <strong>✅ Vista previa:</strong> Mostrando las primeras 5 filas de {data.length} registros
              </p>
            </div>
            <div className="overflow-x-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    {visibleFields.map((field) => (
                      <TableHead key={field.value} className="font-semibold">{field.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewRows.map((row, idx) => (
                    <TableRow key={row.dni || row.name || `row-${idx}`}>
                      {visibleFields.map((field) => (
                        <TableCell key={field.value} className="text-sm">
                          {row[field.value] || <span className="text-slate-600">-</span>}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="p-3 bg-blue-500/10 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>📊 Resumen:</strong> Se importarán {data.length} clientes. Los duplicados por DNI serán omitidos automáticamente.
              </p>
            </div>
            <DialogFooter className="pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>Volver al Mapeo</Button>
              <Button
                onClick={executeImport}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="execute-import-btn"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" />Importando...</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" />Importar {data.length} Clientes</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step 4: Result */}
        {step === 4 && result && (
          <div className="py-6 space-y-6">
            <div className="text-center">
              {result.imported > 0
                ? <CheckCircle className="h-16 w-16 mx-auto text-emerald-500 mb-4" />
                : <XCircle className="h-16 w-16 mx-auto text-orange-500 mb-4" />}
              <h3 className="text-2xl font-bold text-slate-900">Importación Completada</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Card className="border-emerald-200 bg-emerald-500/10">
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-emerald-600">{result.imported}</p>
                  <p className="text-sm text-emerald-700 mt-1">Importados</p>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-amber-500/10">
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-amber-600">{result.duplicates}</p>
                  <p className="text-sm text-amber-700 mt-1">Duplicados (omitidos)</p>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-6 text-center">
                  <p className="text-3xl font-bold text-red-600">{result.errors}</p>
                  <p className="text-sm text-red-700 mt-1">Errores</p>
                </CardContent>
              </Card>
            </div>
            {result.duplicate_dnis && result.duplicate_dnis.length > 0 && (
              <div className="p-4 bg-amber-500/10 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-2">DNIs duplicados omitidos:</p>
                <div className="flex flex-wrap gap-2">
                  {result.duplicate_dnis.slice(0, 10).map((dni) => (
                    <Badge key={dni} variant="outline" className="bg-white">{dni}</Badge>
                  ))}
                  {result.duplicate_dnis.length > 10 && (
                    <Badge variant="secondary">+{result.duplicate_dnis.length - 10} más</Badge>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={handleClose} className="w-full" data-testid="close-import-btn">
                Cerrar
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
